// Crossover Solo — per-panel help data.
//
// Hotspots resolve against `[data-hotspot-id]` attributes on the
// panel's controls. The HotspotLayer measures live element rects
// at runtime, so we never carry static bboxes that drift when the
// panel is re-laid-out.
//
// HARD RULES applied:
//   • #1 source authority — facts traced to docs/sources/equipment/
//     ross-crossover-brochure.pdf and CO-160 (no BTV / CVS / branch).
//   • #2 no fabricated citations — copy paraphrases manuals, no
//     scripture or paragraph numbers attributed.
//   • #8 no internal sources — welcome card is plain prose.

import type { PanelHelpData } from "../../help/help-types";

export const CROSSOVER_SOLO_HELP: PanelHelpData = {
  welcome: {
    title: "Ross Crossover Solo",
    eyebrow: "Video Operations · 1 MLE production switcher",
    body:
      "This is the production switcher you'll operate as Technical Director. It selects which camera or media source goes out to the program feed, lets you arm a different source on preview, and transitions between them on cue. The two horizontal rows of large white buttons are the buses you'll touch most: the upper one is PROGRAM (red — what's on air right now); the lower one is PRESET (green — what you're armed to take next). To the right are the cluster's cut/auto-trans controls and the T-bar.",
    highlights: [
      "PROGRAM bus (red) — the source currently on air.",
      "PRESET bus (green) — armed for the next take; pressing CUT or AUTO TRANS swaps it onto PROGRAM.",
      "CUT vs AUTO TRANS — instant switch vs a smooth dissolve.",
      "FTB — fade to black; emergency / end-of-segment.",
      "DSK — downstream key, used for graphics overlays on top of program.",
      "The full hardware face is shown for orientation; only the labelled controls (buses, CUT, AUTO TRANS, transition type, KEY 1/2/3 TRANS, FTB, DSK) respond. Decorative knobs, dials, and the T-bar are illustrative.",
    ],
  },
  hotspots: [
    {
      id: "multiview",
      targetSelector: "[data-hotspot-id='multiview']",
      label: "Multiview",
      body:
        "Confidence monitor laid out per CO-160a. Top-left tile is PROGRAM (red tally — currently on air); top-right is PREVIEW (green tally — armed for the next take). Smaller tiles show the camera and media sources you can route.",
    },
    {
      id: "pgm-bus",
      targetSelector: "[data-hotspot-id='pgm-bus']",
      label: "PROGRAM bus",
      body:
        "Top row of large buttons. The lit (red) button is the source on air right now. Pressing a different button here is a hot-take — it cuts directly to that source on PROGRAM, bypassing PRESET. Reserve this row for emergencies; the normal workflow is arm-on-PRESET then take.",
    },
    {
      id: "pvw-bus",
      targetSelector: "[data-hotspot-id='pvw-bus']",
      label: "PRESET bus",
      body:
        "Lower row of large buttons. The lit (green) button is the source armed for the next take. Press here to arm; nothing goes on air until you fire CUT, AUTO TRANS, or move the T-bar.",
    },
    {
      id: "joystick",
      targetSelector: "[data-hotspot-id='joystick']",
      label: "Positioner",
      body:
        "Three-axis joystick used for keyer position and DVE moves. On Crossover Solo as configured for CO-160a, the TD generally leaves this alone — keys and effects are pre-set.",
    },
    {
      id: "trans-type",
      targetSelector: "[data-hotspot-id='trans-type']",
      label: "Transition type",
      body:
        "Select the kind of auto-transition: DISS (cross-dissolve), WIPE, or DVE (digital video effect). Default for the convention is DISS — a clean cross-dissolve. WIPE and DVE are not used unless the program specifies otherwise.",
    },
    {
      id: "cut",
      targetSelector: "[data-hotspot-id='cut']",
      label: "CUT",
      body:
        "Instant take. Whatever's armed on PRESET swaps to PROGRAM the moment you press it — no dissolve, no fade. Use for hard cuts on cue. Keyboard: Enter.",
    },
    {
      id: "auto-trans",
      targetSelector: "[data-hotspot-id='auto-trans']",
      label: "AUTO TRANS",
      body:
        "Auto-transition of the configured type and rate (dissolve by default). Pressing this fires a smooth swap from PRESET to PROGRAM over the set duration. Use for soft transitions between speakers, into a video, or out of black. Keyboard: Space.",
    },
    {
      id: "t-bar",
      targetSelector: "[data-hotspot-id='t-bar']",
      label: "T-bar fader",
      body:
        "Manual override for the transition. Drag from one end to the other to perform the same dissolve as AUTO TRANS at your own pace. The TD rarely uses this during a session — it's a rehearsal/recovery tool.",
    },
    {
      id: "key-trans-col",
      targetSelector: "[data-hotspot-id='key-trans-col']",
      label: "KEY TRANS column",
      body:
        "KEY 1 / 2 / 3 TRANS auto-cuts an upstream key on or off air. Used for graphic overlays (lower-thirds, scripture references) without disturbing the PROGRAM source underneath.",
    },
    {
      id: "ftb",
      targetSelector: "[data-hotspot-id='ftb']",
      label: "FTB — Fade to Black",
      body:
        "Fades PROGRAM to black over the configured rate. Used at end of segments and as the standard recovery action if something goes wrong on air. Always pair with the audio team — fading video without muting audio leaves an awkward black hole.",
    },
    {
      id: "dsk",
      targetSelector: "[data-hotspot-id='dsk']",
      label: "DSK — Downstream Key",
      body:
        "Toggles the downstream keyer on or off. The downstream key sits on top of every PROGRAM source — graphics layered here remain visible through cuts and transitions until you DSK it off.",
    },
  ],
  shortcuts: [
    { keys: "1 · 2 · 3 · 7 · 8", action: "Take directly to PROGRAM (CAM 1, CAM 2, CAM 3, MEDIA 1, MEDIA 2)" },
    { keys: "0", action: "Take to BLACK on PROGRAM" },
    { keys: "Q · W · E · U · I", action: "Arm PRESET (CAM 1, CAM 2, CAM 3, MEDIA 1, MEDIA 2)" },
    { keys: "`", action: "Arm BLACK on PRESET" },
    { keys: "Enter", action: "CUT — instant take from PRESET to PROGRAM" },
    { keys: "Space", action: "AUTO TRANS — dissolve from PRESET to PROGRAM" },
    { keys: "Shift + K", action: "Fire the active key (auto-cut)" },
  ],
};
